import React, { useState, useRef } from 'react';

import {
  getLocalWatchlist,
  localStorageEvents
} from '../../../../javascripts/helpers/localstorage';

import Loader from './Loader';
import RelatedTeaser, { RelatedTeaserProps } from './RelatedTeaser';
import WatchListTeaser, { WatchListTeaserProps } from './WatchListTeaser';

const watchlistSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M8 12L2.5 16V0H13.5V16L8 12ZM11.7 12.4652L8 9.77431L4.3 12.4652V1.8H11.7V12.4652Z" fill="black"/></svg>';

type WatchListProps = {
  watchListUrl: string;
  downloadUrl: string;
  hideTeaserButton: boolean;
  sharingBtn: {
    href: string;
    label: string;
  };
  i18n: {
    noEntries: string;
    headline: string;
    relatedArticlesHeadline: string;
    sharingLabel: string;
    sharingText: string;
    downloadLabel: string;
    downloadText: string;
  };
  relatedArticles: RelatedTeaserProps[];
  dummy?: {
    articles: [];
    relatedArticles: [];
  };
};

const WatchList = ({
  i18n,
  watchListUrl,
  downloadUrl,
  hideTeaserButton,
  dummy
}: WatchListProps) => {
  const sharingText = useRef<HTMLDivElement>(null);
  const [localArticles, setLocalArticles] = useState({});
  const [sharingUrl, setSharingUrl] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const [articles, setArticles] = useState<WatchListTeaserProps[]>([]);
  const [relatedArticles, setRelatedArticles] = useState<RelatedTeaserProps[]>(
    []
  );
  const [loaded, setLoaded] = useState(false);
  const [loadedPdf, setLoadedPdf] = useState(true);

  const onCopySharingUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);

      sharingText.current!.style.opacity = '1';
      setTimeout(() => {
        sharingText.current!.style.opacity = '0';
      }, 2000);
    } catch (err) {
      console.error(err);
    }
  };

  const onClickDownloadUrl = async (downloadUrl: string) => {
    const storedWatchlist = getLocalWatchlist();

    setLoadedPdf(false);
    const response = await fetch(downloadUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken || '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        storedWatchlist
      })
    });

    if (!response.ok) {
      setLoadedPdf(true);
      throw new Error('Network response error');
    } else {
      setLoadedPdf(true);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    // Create a temporary anchor element to trigger the download
    const a = document.createElement('a');
    a.href = url;
    a.download = 'KHS-competence.pdf'; // Set the desired file name
    document.body.appendChild(a);
    a.click();

    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onArticleRemoved = (id: string) => {
    const deleteId = (prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    };

    setLocalArticles((prev) => deleteId(prev));
  };

  const fetchCsrfToken = async () => {
    const result = await fetch('/actions/blitz/csrf/token');
    const token = await result.text();
    setCsrfToken(token);
    return token;
  };

  const fetchWatchListData = async (csrfToken: string) => {
    const storedWatchlist = getLocalWatchlist();

    await fetch(watchListUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken || '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        storedWatchlist
      })
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.error) {
          setLoaded(true);
        } else {
          setLocalArticles(response.storedWatchlist.articles);
          setRelatedArticles(response.storedWatchlist.relatedArticles);
          setSharingUrl(response.storedWatchlist.sharingUrl);
          setLoaded(true);
        }
      })
      .catch((err) => {
        console.error(err);
        // Debug data for storybook
        if (dummy && window.location.href.indexOf('localhost:6006') > -1) {
          setTimeout(() => {
            setLocalArticles(dummy.articles);
            setRelatedArticles(dummy.relatedArticles);
            setLoaded(true);
          }, 1500);
        } else {
          setLoaded(true);
        }
      });
  };

  React.useEffect(() => {
    setArticles(
      Object.keys(localArticles).map((articleId) => localArticles[articleId])
    );
  }, [localArticles]);

  React.useEffect(() => {
    const fetchData = async () => {
      const token = await fetchCsrfToken();
      await fetchWatchListData(token);
    };

    fetchData();

    window.addEventListener(localStorageEvents.removed, ((
      ev: CustomEvent<{ id: string }>
    ) => {
      const { id } = ev.detail;
      onArticleRemoved(id);
      // update articles and sharing link after removing
      fetchData();
    }) as EventListener);
  }, []);

  return (
    <>
      <div className="watch-list__list grid">
        <div className="grid-inner">
          <h1 className="watch-list__headline typo-headline-1">
            {i18n.headline}
          </h1>

          {!loaded && !articles.length && (
            <div className="watch-list__loader">
              <Loader />
            </div>
          )}

          {loaded && articles.length === 0 && (
            <p
              className="watch-list__no-entries typo-copy"
              dangerouslySetInnerHTML={{
                __html: i18n.noEntries.replace('{{watchlist}}', watchlistSvg)
              }}
            />
          )}

          {articles.length > 0 && sharingUrl && (
            <div className="watch-list__sharing">
              <div ref={sharingText} className="watch-list__sharing-text">
                <p className="typo-copy">{i18n.sharingText}</p>
              </div>
              <button
                type="button"
                onClick={() => onCopySharingUrl(sharingUrl)}
                className="button button--black button--has-icon"
                title={i18n.sharingLabel}
              >
                <span className="button__icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="Group 7270">
                      <circle
                        id="Ellipse 16"
                        cx="1.5"
                        cy="1.5"
                        r="2.4"
                        transform="matrix(1 8.74228e-08 8.74228e-08 -1 10.8008 14)"
                        stroke="black"
                        strokeWidth="1.8"
                      />
                      <path
                        id="Vector 60"
                        d="M4.80078 9L10.8008 12"
                        stroke="black"
                        strokeWidth="1.8"
                      />
                      <path
                        id="Vector 61"
                        d="M4.80078 7L10.8008 4"
                        stroke="black"
                        strokeWidth="1.8"
                      />
                      <circle
                        id="Ellipse 17"
                        cx="1.5"
                        cy="1.5"
                        r="2.4"
                        transform="matrix(1 8.74228e-08 8.74228e-08 -1 10.8008 5)"
                        stroke="black"
                        strokeWidth="1.8"
                      />
                      <circle
                        id="Ellipse 18"
                        cx="1.5"
                        cy="1.5"
                        r="2.4"
                        transform="matrix(1 8.74228e-08 8.74228e-08 -1 1.80078 9.5)"
                        stroke="black"
                        strokeWidth="1.8"
                      />
                    </g>
                  </svg>
                </span>
                <span className="typo-button button__label">
                  {i18n.sharingLabel}
                </span>
              </button>
            </div>
          )}

          {articles.length > 0 && (
            <div className="watch-list__pdf-generator">
              <div className="watch-list__pdf-generator-inner">
                <div className="watch-list__pdf-generator-btn">
                  <button
                    type="button"
                    onClick={() => onClickDownloadUrl(downloadUrl)}
                    className="link link--greige text-underline"
                    title={i18n.downloadLabel}
                  >
                    <svg
                      width="77"
                      height="70"
                      viewBox="0 0 77 70"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon"
                    >
                      <path
                        d="M9.625 48.125L9.62452 61.2496H67.3745L67.375 48.125H77V70H0V48.125H9.625ZM43.3125 0V33.1874L54.347 23.1564L61.153 29.3436L38.5 49.9372L15.847 29.3436L22.653 23.1564L33.6875 33.1874V0H43.3125Z"
                        fill="currentColor"
                      />
                    </svg>
                    <span className="typo-button font-uppercase">
                      {i18n.downloadLabel}
                    </span>
                  </button>
                  {loadedPdf ? (
                    <p className="typo-copy-small">{i18n.downloadText}</p>
                  ) : (
                    <Loader />
                  )}
                </div>
              </div>
            </div>
          )}

          {articles.length > 0 && (
            <section className="watch-list__teasers">
              {articles.map((item) => (
                <WatchListTeaser
                  key={item.id}
                  {...item}
                  hideTeaserButton={hideTeaserButton}
                />
              ))}
            </section>
          )}
        </div>
      </div>

      {relatedArticles.length > 0 && (
        <div className="watch-list__related-articles teaser-container grid">
          <div className="grid-inner">
            <div className="teaser-container__header">
              <h2 className="teaser-container__headline typo-display-1">
                {i18n.relatedArticlesHeadline}
              </h2>
            </div>
            <div className="teaser-container__teasers">
              {relatedArticles.map((item) => (
                <RelatedTeaser
                  key={item.id}
                  {...item}
                  hideTeaserButton={hideTeaserButton}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WatchList;
